import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import {
  getCycleCounts, createCycleCount, createCycleCountBySku,
  getCycleCount, approveCycleCount, rejectCycleCount,
  completeCycleCount, countItem, getSkus,
  getCycleCountLeaderboard, publishCycleCountLeaderboard,
} from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from '../components/ui/dialog';
import SkuVariantPicker from '../components/SkuVariantPicker';
import {
  ClipboardText, Plus, Gavel, Check, X, Barcode, Warning, DeviceMobile,
  Trophy, PaperPlaneTilt, CaretDown, CaretRight, Lightning, MagnifyingGlass,
} from '@phosphor-icons/react';

const STATUS_STYLES = {
  pending: 'bg-amber-100 text-amber-800',
  in_progress: 'bg-blue-100 text-blue-800',
  pending_review: 'bg-purple-100 text-purple-800 ring-1 ring-purple-300',
  completed: 'bg-green-100 text-green-800',
  approved: 'bg-emerald-100 text-emerald-800',
  rejected: 'bg-rose-100 text-rose-800',
};

export default function CycleCounts() {
  const { user } = useAuth();
  const isManager = user?.role === 'admin' || user?.role === 'manager';
  const canCount = isManager || user?.role === 'picker';

  const [counts, setCounts] = useState([]);
  const [ccSearch, setCcSearch] = useState('');
  const visibleCounts = useMemo(() => {
    const q = ccSearch.trim().toLowerCase();
    if (!q) return counts;
    return counts.filter((cc) =>
      (cc.count_number || '').toLowerCase().includes(q) ||
      (cc.count_type || '').toLowerCase().includes(q)
    );
  }, [counts, ccSearch]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');

  // New-count dialog
  const [showCreate, setShowCreate] = useState(false);
  const [createMode, setCreateMode] = useState('full'); // 'full' | 'sku'
  const [selectedSku, setSelectedSku] = useState('');
  const [skus, setSkus] = useState([]);
  // (legacy `skuSearch` state was removed — SkuVariantPicker owns its own search input now.)

  // Count-item (data entry) dialog
  const [activeCount, setActiveCount] = useState(null);
  const [activeCountDetail, setActiveCountDetail] = useState(null);
  const [entryBusy, setEntryBusy] = useState(false);
  const [entryValues, setEntryValues] = useState({});

  // Review dialog
  const [reviewDetail, setReviewDetail] = useState(null);
  const [reviewNote, setReviewNote] = useState('');
  const [reviewBusy, setReviewBusy] = useState(false);

  // Leaderboard
  const [leaderboard, setLeaderboard] = useState(null);
  const [lbLookback, setLbLookback] = useState(30);
  const [lbOpen, setLbOpen] = useState(true);
  const [publishBusy, setPublishBusy] = useState(false);

  const fetchCounts = async () => {
    setLoading(true);
    try {
      const { data } = await getCycleCounts(statusFilter ? { status: statusFilter } : {});
      setCounts(data);
    } catch (e) {
      toast.error('Failed to load cycle counts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCounts(); }, [statusFilter]);

  useEffect(() => {
    getSkus().then(r => setSkus(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    getCycleCountLeaderboard(lbLookback, 10)
      .then(r => setLeaderboard(r.data))
      .catch(() => setLeaderboard(null));
  }, [lbLookback]);

  const handlePublish = async () => {
    if (!isManager) return;
    setPublishBusy(true);
    try {
      const { data } = await publishCycleCountLeaderboard(lbLookback);
      if (data.skipped) {
        toast(`Not enough qualifying pickers yet (${data.total_pickers} total)`);
      } else {
        toast.success(`Broadcast sent · top ${data.top_count} picker(s)`);
      }
    } catch (e) {
      toast.error('Publish failed');
    } finally {
      setPublishBusy(false);
    }
  };

  const summary = useMemo(() => {
    const by = {};
    for (const c of counts) by[c.status] = (by[c.status] || 0) + 1;
    return by;
  }, [counts]);

  const handleCreate = async () => {
    try {
      if (createMode === 'sku') {
        if (!selectedSku) { toast.error('Select a SKU'); return; }
        const { data } = await createCycleCountBySku(selectedSku);
        toast.success(`Count ${data.count_number} seeded · ${data.locations_targeted} location(s)`);
      } else {
        const { data } = await createCycleCount({ count_type: 'full', location_ids: [], sku_ids: [] });
        toast.success(`Count ${data.count_number} created`);
      }
      setShowCreate(false);
      setSelectedSku('');
      setCreateMode('full');
      fetchCounts();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create count');
    }
  };

  const openEntry = async (cc) => {
    setActiveCount(cc);
    setActiveCountDetail(null);
    setEntryValues({});
    try {
      const { data } = await getCycleCount(cc.id);
      setActiveCountDetail(data);
    } catch (e) {
      toast.error('Failed to load count detail');
    }
  };

  const submitItem = async (target) => {
    const key = `${target.location_id}:${target.variant_id}`;
    const qty = parseInt(entryValues[key], 10);
    if (Number.isNaN(qty) || qty < 0) {
      toast.error('Enter a valid non-negative count');
      return;
    }
    setEntryBusy(true);
    try {
      await countItem(activeCount.id, {
        location_id: target.location_id,
        variant_id: target.variant_id,
        counted_quantity: qty,
      });
      // refresh detail
      const { data } = await getCycleCount(activeCount.id);
      setActiveCountDetail(data);
      setEntryValues(v => ({ ...v, [key]: '' }));
      toast.success(`Counted ${qty} · variance refreshed`);
    } catch (e) {
      toast.error('Count item failed');
    } finally {
      setEntryBusy(false);
    }
  };

  const submitComplete = async () => {
    if (!activeCount) return;
    setEntryBusy(true);
    try {
      const { data } = await completeCycleCount(activeCount.id);
      toast.success(data.message);
      setActiveCount(null);
      setActiveCountDetail(null);
      fetchCounts();
    } catch (e) {
      toast.error('Complete failed');
    } finally {
      setEntryBusy(false);
    }
  };

  const openReview = async (cc) => {
    setReviewNote('');
    setReviewDetail({ loading: true, id: cc.id });
    try {
      const { data } = await getCycleCount(cc.id);
      setReviewDetail(data);
    } catch (e) {
      toast.error('Failed to load review detail');
      setReviewDetail(null);
    }
  };

  const handleApprove = async () => {
    if (!reviewDetail?.id) return;
    setReviewBusy(true);
    try {
      const { data } = await approveCycleCount(reviewDetail.id, reviewNote);
      toast.success(`Approved · ${data.adjustments_applied} adjustment(s) applied`);
      setReviewDetail(null);
      fetchCounts();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Approve failed');
    } finally {
      setReviewBusy(false);
    }
  };

  const handleReject = async () => {
    if (!reviewDetail?.id) return;
    setReviewBusy(true);
    try {
      await rejectCycleCount(reviewDetail.id, reviewNote);
      toast.success('Rejected · inventory unchanged');
      setReviewDetail(null);
      fetchCounts();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Reject failed');
    } finally {
      setReviewBusy(false);
    }
  };

  return (
    <Layout>
      <div data-testid="cycle-counts-page" className="p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
              <ClipboardText size={32} weight="fill" className="text-brand" />
              Cycle Counts
            </h1>
            <p className="text-muted-foreground">Recurring inventory audits with management review on variance.</p>
          </div>
          {isManager && (
            <Dialog open={showCreate} onOpenChange={setShowCreate}>
              <DialogTrigger asChild>
                <Button data-testid="new-cycle-count-btn" className="bg-brand hover:bg-brand/90 rounded-none">
                  <Plus size={16} weight="bold" className="mr-2" /> New Count
                </Button>
              </DialogTrigger>
              <DialogContent className="border border-border rounded-none max-w-md" data-testid="new-cycle-count-dialog">
                <DialogHeader>
                  <DialogTitle>New Cycle Count</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="flex gap-2 p-1 bg-muted border border-border">
                    <button
                      type="button"
                      data-testid="mode-full"
                      onClick={() => setCreateMode('full')}
                      className={`flex-1 px-3 py-2 text-xs font-bold uppercase tracking-widest transition ${
                        createMode === 'full' ? 'bg-brand text-white' : 'text-muted-foreground'
                      }`}
                    >
                      Full / Manual
                    </button>
                    <button
                      type="button"
                      data-testid="mode-sku"
                      onClick={() => setCreateMode('sku')}
                      className={`flex-1 px-3 py-2 text-xs font-bold uppercase tracking-widest transition ${
                        createMode === 'sku' ? 'bg-brand text-white' : 'text-muted-foreground'
                      }`}
                    >
                      By SKU
                    </button>
                  </div>

                  {createMode === 'sku' ? (
                    <div className="space-y-2">
                      <Label className="overline">Pick a SKU</Label>
                      <SkuVariantPicker
                        skus={skus}
                        variants={[]}
                        value={selectedSku}
                        onChange={setSelectedSku}
                        mode="sku"
                        testIdPrefix="cc-sku-picker"
                        placeholder="Search SKU code or name…"
                      />
                      <p className="text-xs text-muted-foreground">
                        <Barcode size={12} weight="bold" className="inline mr-1" />
                        Seeds the count with every location where this SKU currently has available stock.
                      </p>
                    </div>
                  ) : (
                    <div className="text-sm text-muted-foreground bg-muted border border-border p-3">
                      Creates a blank count. Add items one by one from the detail view.
                    </div>
                  )}

                  <Button
                    onClick={handleCreate}
                    disabled={createMode === 'sku' && !selectedSku}
                    data-testid="create-cycle-count-btn"
                    className="w-full bg-brand hover:bg-brand/90 rounded-none"
                  >
                    Create Count
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {/* Leaderboard */}
        <div data-testid="leaderboard-widget" className="bg-gradient-to-br from-amber-50 via-white to-slate-50 border border-amber-200 mb-5">
          <button
            onClick={() => setLbOpen(o => !o)}
            data-testid="leaderboard-toggle"
            className="w-full flex items-center justify-between px-4 py-3 border-b border-amber-100"
          >
            <div className="flex items-center gap-3">
              <div className="bg-amber-100 border border-amber-300 p-2">
                <Trophy size={20} weight="fill" className="text-amber-600" />
              </div>
              <div className="text-left">
                <p className="overline text-amber-700">Streaks</p>
                <h2 className="text-lg font-bold tracking-tight">
                  Cycle-count Leaderboard
                </h2>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right hidden md:block">
                <p className="overline">Qualifying</p>
                <p className="text-lg font-bold font-mono" data-testid="leaderboard-qualifying">
                  {leaderboard?.qualifying_pickers ?? 0}
                </p>
              </div>
              <div className="text-right hidden md:block">
                <p className="overline">Items</p>
                <p className="text-lg font-bold font-mono">{leaderboard?.total_items ?? 0}</p>
              </div>
              <div className="flex bg-card border border-amber-200">
                {[7, 30, 90].map(d => (
                  <span
                    key={d}
                    role="button"
                    tabIndex={0}
                    onClick={(e) => { e.stopPropagation(); setLbLookback(d); }}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); setLbLookback(d); } }}
                    data-testid={`lb-window-${d}`}
                    className={`px-3 py-1.5 text-xs font-bold uppercase tracking-widest cursor-pointer transition select-none ${
                      lbLookback === d
                        ? 'bg-amber-600 text-white'
                        : 'text-muted-foreground hover:text-amber-700'
                    }`}
                  >
                    {d}d
                  </span>
                ))}
              </div>
              {lbOpen ? <CaretDown size={16} weight="bold" /> : <CaretRight size={16} weight="bold" />}
            </div>
          </button>
          {lbOpen && (
            <div className="p-4">
              {!leaderboard ? (
                <div className="py-8 text-center text-muted-foreground text-sm">Loading…</div>
              ) : (leaderboard.rows || []).length === 0 ? (
                <div className="py-8 text-center text-muted-foreground text-sm" data-testid="leaderboard-empty">
                  No cycle-count data in the last {lbLookback} days yet. Start a count to populate the board.
                </div>
              ) : (
                <>
                  {(() => {
                    const podium = leaderboard.rows.filter(r => r.rank).slice(0, 3);
                    if (podium.length === 0) return null;
                    const gridCols = podium.length === 1
                      ? 'grid-cols-1'
                      : podium.length === 2 ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1 md:grid-cols-3';
                    return (
                      <div className={`grid ${gridCols} gap-3 mb-4`}>
                        {podium.map((r, i) => {
                          const medal = ['🥇', '🥈', '🥉'][i] || '';
                          const borders = ['border-amber-400 bg-amber-50', 'border-border bg-muted', 'border-orange-300 bg-orange-50'];
                          return (
                            <div
                              key={r.user_id}
                              data-testid={`leaderboard-podium-${r.rank}`}
                              className={`border-2 p-3 ${borders[i]}`}
                            >
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-3xl">{medal}</span>
                                <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                                  #{r.rank}
                                </span>
                              </div>
                              <div className="font-bold text-base truncate">
                                {r.name}
                              </div>
                              <div className="flex items-baseline gap-2 mt-2">
                                <span className="text-2xl font-bold font-mono text-amber-700">
                                  {r.accuracy_pct.toFixed(1)}%
                                </span>
                                <span className="text-xs text-muted-foreground">accuracy</span>
                              </div>
                              <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
                                <span>{r.items_counted} items</span>
                                {r.median_seconds_per_item !== null && r.median_seconds_per_item !== undefined && (
                                  <span className="flex items-center gap-1">
                                    <Lightning size={10} weight="fill" className="text-amber-500" />
                                    {r.median_seconds_per_item.toFixed(0)}s
                                  </span>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}

                  {leaderboard.rows.length > 3 && (
                    <div className="border border-border bg-card overflow-x-auto">
                      <table className="w-full table-dense">
                        <thead>
                          <tr>
                            <th className="w-10">#</th>
                            <th>Picker</th>
                            <th className="text-right">Items</th>
                            <th className="text-right">Matches</th>
                            <th className="text-right">Accuracy</th>
                            <th className="text-right">Speed</th>
                            <th className="text-right">Score</th>
                          </tr>
                        </thead>
                        <tbody>
                          {leaderboard.rows.slice(3).map(r => (
                            <tr key={r.user_id} data-testid={`leaderboard-row-${r.user_id}`}>
                              <td className="font-mono font-bold text-muted-foreground">
                                {r.rank ? `#${r.rank}` : '—'}
                              </td>
                              <td>
                                <div className="font-bold">{r.name}</div>
                                <div className="text-xs text-muted-foreground uppercase tracking-widest">{r.role}</div>
                              </td>
                              <td className="font-mono text-right">{r.items_counted}</td>
                              <td className="font-mono text-right text-green-600">{r.matches}</td>
                              <td className="font-mono text-right font-bold">{r.accuracy_pct.toFixed(1)}%</td>
                              <td className="font-mono text-right text-muted-foreground">
                                {r.median_seconds_per_item != null ? `${r.median_seconds_per_item.toFixed(0)}s` : '—'}
                              </td>
                              <td className="font-mono text-right font-bold text-amber-700">
                                {r.composite_score.toFixed(1)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {isManager && (
                    <div className="flex items-center justify-between mt-3 pt-3 border-t border-amber-100">
                      <p className="text-xs text-muted-foreground">
                        Ranking = 70% accuracy + 30% speed · min 5 items to qualify · auto-publishes to Slack weekly (Mon 09:00 UTC).
                      </p>
                      <Button
                        onClick={handlePublish}
                        disabled={publishBusy}
                        data-testid="leaderboard-publish-btn"
                        className="bg-amber-600 hover:bg-amber-700 text-white rounded-none"
                      >
                        <PaperPlaneTilt size={14} weight="bold" className="mr-1" />
                        Publish now
                      </Button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {/* Summary bar */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mb-5">
          {[
            ['all', counts.length, 'bg-muted border-border'],
            ['pending', summary.pending || 0, 'bg-amber-50 border-amber-200'],
            ['in_progress', summary.in_progress || 0, 'bg-blue-50 border-blue-200'],
            ['pending_review', summary.pending_review || 0, 'bg-purple-50 border-purple-200'],
            ['approved', summary.approved || 0, 'bg-emerald-50 border-emerald-200'],
            ['rejected', summary.rejected || 0, 'bg-rose-50 border-rose-200'],
          ].map(([k, v, bg]) => (
            <button
              key={k}
              onClick={() => setStatusFilter(k === 'all' ? '' : k)}
              data-testid={`cc-filter-${k}`}
              className={`${bg} border p-3 text-left transition ${statusFilter === k || (k === 'all' && !statusFilter) ? 'ring-2 ring-brand' : ''}`}
            >
              <div className="text-xs font-bold uppercase tracking-widest text-muted-foreground">{k.replace('_', ' ')}</div>
              <div className="text-2xl font-bold font-mono">{v}</div>
            </button>
          ))}
        </div>

        {/* Job-number search */}
        <div className="relative" data-testid="cc-search-bar">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            data-testid="cc-search"
            placeholder="Search count number…"
            value={ccSearch}
            onChange={(e) => setCcSearch(e.target.value)}
            className="pl-9 h-10 font-mono"
            autoComplete="off"
          />
        </div>

        <div className="bg-card border border-border rounded-lg overflow-x-auto">
          <table className="w-full table-dense">
            <thead>
              <tr>
                <th>Count #</th>
                <th>Type</th>
                <th>Status</th>
                <th className="text-right">Items</th>
                <th className="text-right">Discrepancies</th>
                <th>Created</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="text-center py-10 text-muted-foreground">Loading…</td></tr>
              ) : visibleCounts.length === 0 ? (
                <tr><td colSpan={7} className="text-center py-10 text-muted-foreground" data-testid="cc-empty">{ccSearch ? `No counts match "${ccSearch}".` : 'No cycle counts yet.'}</td></tr>
              ) : visibleCounts.map(cc => (
                <tr key={cc.id} data-testid={`cycle-count-row-${cc.count_number}`}>
                  <td className="font-mono font-semibold">{cc.count_number}</td>
                  <td className="uppercase text-xs font-bold text-muted-foreground">{cc.count_type}</td>
                  <td>
                    <span className={`px-2 py-1 text-xs font-bold uppercase ${STATUS_STYLES[cc.status] || 'bg-muted text-slate-800'}`}>
                      {cc.status?.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="font-mono text-right">{cc.counted_items?.length || 0}</td>
                  <td className="font-mono text-right">
                    {cc.discrepancy_count ? (
                      <span className="text-red-600 font-bold inline-flex items-center gap-1 justify-end">
                        <Warning size={12} weight="fill" /> {cc.discrepancy_count}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="text-xs text-muted-foreground">{new Date(cc.created_at).toLocaleDateString()}</td>
                  <td className="text-right space-x-2">
                    {['pending', 'in_progress'].includes(cc.status) && canCount && (
                      <>
                        <Link
                          to={`/cycle-counts/${cc.id}/count`}
                          data-testid={`count-mode-${cc.count_number}`}
                          className="inline-flex items-center px-3 py-1 text-xs font-bold uppercase tracking-widest border border-[#66CC8A] text-[#2F9B5E] hover:bg-[#66CC8A] hover:text-slate-950 transition"
                        >
                          <DeviceMobile size={12} weight="bold" className="mr-1" /> Count mode
                        </Link>
                        {isManager && (
                          <button
                            onClick={() => openEntry(cc)}
                            data-testid={`enter-counts-${cc.count_number}`}
                            className="px-3 py-1 text-xs font-bold uppercase tracking-widest border border-brand text-brand hover:bg-brand hover:text-white transition"
                          >
                            Desk entry
                          </button>
                        )}
                      </>
                    )}
                    {cc.status === 'pending_review' && isManager && (
                      <button
                        onClick={() => openReview(cc)}
                        data-testid={`review-cycle-count-${cc.count_number}`}
                        className="px-3 py-1 text-xs font-bold uppercase tracking-widest border border-purple-600 text-purple-700 hover:bg-purple-600 hover:text-white transition"
                      >
                        <Gavel size={12} weight="bold" className="inline mr-1" /> Review
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Data entry dialog */}
        <Dialog open={!!activeCount} onOpenChange={(o) => { if (!o) { setActiveCount(null); setActiveCountDetail(null); } }}>
          <DialogContent className="border border-border rounded-none max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="cycle-count-entry-dialog">
            <DialogHeader>
              <DialogTitle>
                Enter counts · {activeCount?.count_number}
              </DialogTitle>
            </DialogHeader>
            {!activeCountDetail ? (
              <div className="py-12 text-center text-muted-foreground">Loading…</div>
            ) : (
              <div className="space-y-4">
                {activeCountDetail.count_type === 'sku' && (
                  <div className="bg-blue-50 border border-blue-200 p-3 text-sm">
                    Targeting <b>{activeCountDetail.seeded_from?.sku_code}</b> across {activeCountDetail.location_ids?.length} location(s).
                  </div>
                )}
                {activeCountDetail.pending_targets?.length > 0 && (
                  <div>
                    <p className="overline mb-2">Remaining targets</p>
                    <div className="border border-border max-h-[280px] overflow-y-auto">
                      <table className="w-full table-dense">
                        <thead className="sticky top-0 bg-card">
                          <tr>
                            <th>Location</th>
                            <th>SKU</th>
                            <th className="text-right">Counted</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {activeCountDetail.pending_targets.map(t => {
                            const key = `${t.location_id}:${t.variant_id}`;
                            return (
                              <tr key={key}>
                                <td className="font-mono text-sm">{t.location_code}</td>
                                <td className="font-mono text-brand text-sm">{t.sku_code}</td>
                                <td className="text-right">
                                  <Input
                                    type="number"
                                    min="0"
                                    value={entryValues[key] ?? ''}
                                    onChange={(e) => setEntryValues(v => ({ ...v, [key]: e.target.value }))}
                                    className="w-24 ml-auto border-border"
                                    data-testid={`qty-input-${t.sku_code}`}
                                  />
                                </td>
                                <td>
                                  <Button
                                    size="sm"
                                    disabled={entryBusy || entryValues[key] === '' || entryValues[key] == null}
                                    onClick={() => submitItem(t)}
                                    data-testid={`submit-qty-${t.sku_code}`}
                                    className="bg-brand hover:bg-brand/90 rounded-none text-xs"
                                  >
                                    Save
                                  </Button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {activeCountDetail.counted_items?.length > 0 && (
                  <div>
                    <p className="overline mb-2">Counted so far ({activeCountDetail.counted_items.length})</p>
                    <div className="border border-border max-h-[200px] overflow-y-auto">
                      <table className="w-full table-dense">
                        <thead className="sticky top-0 bg-card">
                          <tr>
                            <th>Location</th>
                            <th>SKU</th>
                            <th className="text-right">Expected</th>
                            <th className="text-right">Counted</th>
                            <th className="text-right">Variance</th>
                          </tr>
                        </thead>
                        <tbody>
                          {activeCountDetail.counted_items.map((it, i) => (
                            <tr key={i} className={it.variance !== 0 ? 'bg-red-50' : ''}>
                              <td className="font-mono text-sm">{it.location_code}</td>
                              <td className="font-mono text-brand text-sm">{it.sku_code}</td>
                              <td className="font-mono text-right text-muted-foreground">{it.expected_quantity}</td>
                              <td className="font-mono text-right font-bold">{it.counted_quantity}</td>
                              <td className={`font-mono text-right font-bold ${it.variance > 0 ? 'text-green-600' : it.variance < 0 ? 'text-red-600' : 'text-muted-foreground'}`}>
                                {it.variance > 0 ? '+' : ''}{it.variance}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                <div className="flex items-center justify-end gap-2 pt-2 border-t border-border">
                  <Button
                    variant="outline"
                    onClick={() => { setActiveCount(null); setActiveCountDetail(null); }}
                    className="rounded-none"
                  >
                    Close
                  </Button>
                  <Button
                    onClick={submitComplete}
                    disabled={entryBusy || (activeCountDetail.counted_items?.length || 0) === 0}
                    data-testid="complete-count-btn"
                    className="bg-brand hover:bg-brand/90 rounded-none"
                  >
                    Complete count
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Review dialog (pending_review → approve/reject) */}
        <Dialog open={!!reviewDetail} onOpenChange={(o) => !o && setReviewDetail(null)}>
          <DialogContent className="border border-border rounded-none max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="cycle-count-review-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Gavel size={20} weight="fill" className="text-purple-600" />
                Review · {reviewDetail?.count_number || '…'}
              </DialogTitle>
            </DialogHeader>
            {reviewDetail?.loading ? (
              <div className="py-12 text-center text-muted-foreground">Loading…</div>
            ) : reviewDetail ? (
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-muted border border-border p-3">
                    <p className="overline">Items</p>
                    <p className="text-2xl font-bold font-mono">{reviewDetail.counted_items?.length || 0}</p>
                  </div>
                  <div className="bg-red-50 border border-red-200 p-3">
                    <p className="overline">Discrepancies</p>
                    <p className="text-2xl font-bold font-mono text-red-600" data-testid="review-discrepancy-count">
                      {reviewDetail.discrepancy_count || 0}
                    </p>
                  </div>
                  <div className="bg-amber-50 border border-amber-200 p-3">
                    <p className="overline">|Variance|</p>
                    <p className="text-2xl font-bold font-mono text-amber-700">{reviewDetail.total_variance_abs || 0}</p>
                  </div>
                </div>

                <div className="border border-border overflow-x-auto max-h-[320px] overflow-y-auto">
                  <table className="w-full table-dense">
                    <thead className="sticky top-0 bg-card">
                      <tr>
                        <th>Location</th>
                        <th>SKU</th>
                        <th className="text-right">Expected</th>
                        <th className="text-right">Counted</th>
                        <th className="text-right">Variance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(reviewDetail.counted_items || []).map((it, i) => {
                        const variance = it.variance || 0;
                        return (
                          <tr key={i} className={variance !== 0 ? 'bg-red-50' : ''}>
                            <td className="font-mono text-sm">{it.location_code || it.location_id?.slice(-6)}</td>
                            <td className="font-mono text-brand text-sm">{it.sku_code || it.variant_id?.slice(-6)}</td>
                            <td className="font-mono text-right text-muted-foreground">{it.expected_quantity}</td>
                            <td className="font-mono text-right font-bold">{it.counted_quantity}</td>
                            <td className={`font-mono text-right font-bold ${variance > 0 ? 'text-green-600' : variance < 0 ? 'text-red-600' : 'text-muted-foreground'}`}>
                              {variance > 0 ? '+' : ''}{variance}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <div>
                  <Label className="overline">Review note (optional)</Label>
                  <Input
                    value={reviewNote}
                    onChange={(e) => setReviewNote(e.target.value)}
                    placeholder="e.g. Counted twice — confirmed delta"
                    className="mt-1 border-border"
                    data-testid="review-note-input"
                  />
                </div>

                <div className="flex items-center justify-end gap-2 pt-2 border-t border-border">
                  <Button
                    variant="outline"
                    onClick={handleReject}
                    disabled={reviewBusy}
                    data-testid="reject-cycle-count-btn"
                    className="border-rose-300 text-rose-700 hover:bg-rose-50 rounded-none"
                  >
                    <X size={14} weight="bold" className="mr-1" /> Reject · keep inventory
                  </Button>
                  <Button
                    onClick={handleApprove}
                    disabled={reviewBusy}
                    data-testid="approve-cycle-count-btn"
                    className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-none"
                  >
                    <Check size={14} weight="bold" className="mr-1" /> Approve · apply adjustments
                  </Button>
                </div>
              </div>
            ) : null}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
